import DatePicker from "@/components/DatePicker";
import { toaster } from "@/components/ui/toaster";
import { date, now } from "@/helpers/time";
import { useMerchant } from "@/hooks/useMerchant";
import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  HStack,
  Heading,
  Input,
  Portal,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { propertyUseLabels } from "../../screenings/mappers/screening.mapper";
import { useProtectTRPC } from "../../trpc";
import { ChangeResult, formatPercent, increaseCap, maxRent } from "../changes";
import { formatCalendarDate, formatMonth } from "../columns";
import { Contract } from "../types";
import { useChangeRules } from "../useChangeRules";
import { useInvalidateContracts } from "../useInvalidateContracts";
import ChangeOutcome from "./ChangeOutcome";

type Props = {
  open: boolean;
  contracts: Contract[];
  onClose: (changed: boolean) => void;
  onExitComplete: () => void;
};

type Row = { monthlyRent: number; rentalsContractEndDate?: string };

const COLUMNS = [
  "Inmueble",
  "Tipo",
  "Canon actual",
  "Nuevo canon",
  "Vence",
  "Nueva fecha fin",
  "",
];

const parsePercent = (value: string) => {
  const percent = Number(value.trim().replace(",", "."));

  return value.trim() && Number.isFinite(percent) && percent > 0
    ? percent / 100
    : undefined;
};

const RenewModal = ({
  open,
  contracts,
  onClose,
  onExitComplete,
}: Props): JSX.Element => {
  const trpc = useProtectTRPC();
  const { merchant } = useMerchant();
  const invalidateContracts = useInvalidateContracts();
  const rules = useChangeRules();
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");
  const formatNumber = useNumberFormatter("es-CO", "COP", "number");

  const [rows, setRows] = useState<Record<string, Row>>(() =>
    Object.fromEntries(
      contracts.map((contract) => [
        contract.id,
        { monthlyRent: contract.monthlyRent },
      ])
    )
  );
  const [percent, setPercent] = useState("");
  const [results, setResults] = useState<ChangeResult[]>();
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const { mutate: renew, isPending } = useMutation({
    ...trpc.contracts.renew.mutationOptions(),
    onSuccess: (data) => {
      setResults(data.results);
      invalidateContracts();
    },
    onError: () =>
      toaster.create({
        title: "Error",
        description: "No pudimos aplicar los cambios. Intenta de nuevo.",
        type: "error",
        duration: 5000,
        meta: { closable: true },
      }),
  });

  const updateRow = (contractId: string, update: Partial<Row>) =>
    setRows((prev) => ({
      ...prev,
      [contractId]: { ...prev[contractId], ...update },
    }));

  const items = contracts.flatMap((contract) => {
    const row = rows[contract.id];
    const changesRent = row.monthlyRent !== contract.monthlyRent;
    const changesEndDate =
      !!row.rentalsContractEndDate &&
      row.rentalsContractEndDate !== contract.rentalsContractEndDate;

    return changesRent || changesEndDate
      ? [{ contractId: contract.id, ...row }]
      : [];
  });
  const hasEmptyRent = items.some(({ monthlyRent }) => monthlyRent === 0);

  const ipc = rules?.ipc ?? null;
  const percentValue = parsePercent(percent);
  const isOverIpc =
    percentValue !== undefined && ipc !== null && percentValue > ipc;

  const applyPercent = () => {
    if (percentValue === undefined || ipc === null || isOverIpc) return;

    contracts
      .filter((contract) => contract.propertyUse === "residential")
      .forEach((contract) =>
        updateRow(contract.id, {
          monthlyRent: maxRent(contract.monthlyRent, percentValue),
        })
      );
  };

  const suggestEndDates = () =>
    contracts.forEach((contract) =>
      updateRow(contract.id, {
        rentalsContractEndDate: date(contract.rentalsContractEndDate)
          .add(12, "month")
          .format("YYYY-MM-DD"),
      })
    );

  const changed = !!results?.some((result) => result.outcome !== "rejected");
  const close = () => {
    if (!isPending) onClose(changed);
  };

  const handleSubmit = () => {
    if (!merchant || items.length === 0) return;

    renew({ rentalsMerchantId: merchant.id, items });
  };

  const appliesNow = rules?.effectiveMonth === now().format("YYYY-MM");

  return (
    <Dialog.Root
      open={open && isMounted}
      onOpenChange={(e) => !e.open && close()}
      onExitComplete={onExitComplete}
      size="xl"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottom="1px solid #ebecef80" padding="4">
              <Dialog.Title>
                <Heading>Renovar / Aumentar canon</Heading>
                <Text textStyle="sm" color="gray.500" fontWeight="normal">
                  {contracts.length} contrato(s) · revisa el precio de cada
                  inmueble
                </Text>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton marginTop="1" disabled={isPending} />
            </Dialog.CloseTrigger>
            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                {!results && (
                  <Box>
                    <Text fontWeight={600} fontSize="sm" mb={2}>
                      % a vivienda · tope IPC{" "}
                      {ipc !== null ? formatPercent(ipc) : "sin cargar"}
                    </Text>
                    <HStack gap={2} wrap="wrap">
                      <Input
                        size="sm"
                        width="120px"
                        inputMode="decimal"
                        placeholder="ej: 5,28"
                        value={percent}
                        onChange={(e) => setPercent(e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          percentValue === undefined ||
                          ipc === null ||
                          isOverIpc
                        }
                        onClick={applyPercent}
                      >
                        Aplicar a vivienda
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={suggestEndDates}
                      >
                        Sugerir vigencia +12m
                      </Button>
                    </HStack>
                    {isOverIpc && ipc !== null && (
                      <Text fontSize="sm" color="#EC5858" mt={1}>
                        Supera el tope IPC de {formatPercent(ipc)}.
                      </Text>
                    )}
                  </Box>
                )}
                {rules && (
                  <Box bg="gray.50" borderRadius="md" p={3}>
                    <Text fontSize="sm" color="gray.700">
                      <b>Vivienda</b> se topa al IPC y <b>comercio</b> al{" "}
                      {formatPercent(rules.commercialMaxIncrease)}, con máximo
                      un aumento cada 12 meses. Procura{" "}
                      <b>mantener al día la fecha de fin</b>: nos ayuda a saber
                      cuándo vence cada contrato. Si no la cambias, se queda
                      como está.
                    </Text>
                    <Text fontSize="sm" color="gray.700" mt={1}>
                      {appliesNow
                        ? `Los cambios aplican hoy y cuentan desde ${formatMonth(
                            rules.effectiveMonth
                          )}.`
                        : `Pasó el corte de este mes: los cambios quedan programados para ${formatMonth(
                            rules.effectiveMonth
                          )}.`}
                    </Text>
                  </Box>
                )}
                <Table.ScrollArea>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="#fcfcfd">
                        {COLUMNS.map((label) => (
                          <Table.ColumnHeader key={label} color="gray.500">
                            {label}
                          </Table.ColumnHeader>
                        ))}
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {contracts.map((contract) => {
                        const row = rows[contract.id];
                        const result = results?.find(
                          ({ contractId }) => contractId === contract.id
                        );
                        const cap = rules ? increaseCap(contract, rules) : null;
                        const max =
                          cap !== null
                            ? maxRent(contract.monthlyRent, cap)
                            : undefined;

                        return (
                          <Table.Row key={contract.id}>
                            <Table.Cell>
                              <Text fontWeight={600}>
                                {contract.tenantName}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {contract.propertyAddress || "Sin dirección"}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge bg="#DFEAFF" color="#6887D8" size="md">
                                {propertyUseLabels[contract.propertyUse]}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              {formatCurrency(contract.monthlyRent)}
                            </Table.Cell>
                            <Table.Cell>
                              <Input
                                size="sm"
                                width="140px"
                                inputMode="numeric"
                                disabled={!!results}
                                value={formatNumber(row.monthlyRent)}
                                onChange={(e) =>
                                  updateRow(contract.id, {
                                    monthlyRent: Number(
                                      e.target.value.replace(/\D/g, "")
                                    ),
                                  })
                                }
                              />
                              {row.monthlyRent === 0 && (
                                <Text fontSize="xs" mt={1} color="#EC5858">
                                  Escribe el nuevo canon.
                                </Text>
                              )}
                              {max !== undefined &&
                                row.monthlyRent > contract.monthlyRent && (
                                  <Text
                                    fontSize="xs"
                                    mt={1}
                                    color={
                                      row.monthlyRent > max
                                        ? "#EC5858"
                                        : "gray.500"
                                    }
                                  >
                                    +
                                    {formatPercent(
                                      row.monthlyRent / contract.monthlyRent - 1
                                    )}{" "}
                                    · máx. {formatCurrency(max)}
                                  </Text>
                                )}
                            </Table.Cell>
                            <Table.Cell>
                              {formatCalendarDate(
                                contract.rentalsContractEndDate
                              )}
                            </Table.Cell>
                            <Table.Cell minWidth="170px">
                              <DatePicker
                                placeholder="Sin cambio"
                                value={row.rentalsContractEndDate}
                                setValue={(value) =>
                                  updateRow(contract.id, {
                                    rentalsContractEndDate: value,
                                  })
                                }
                                minDate={date(contract.rentalsContractEndDate)
                                  .add(1, "day")
                                  .toDate()}
                                isDisabled={!!results}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              {result && (
                                <ChangeOutcome
                                  result={result}
                                  scheduledLabel={(month) =>
                                    `Programado para ${month}`
                                  }
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80">
              {results ? (
                <Button onClick={close}>Cerrar</Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={close}
                    disabled={isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={items.length === 0 || hasEmptyRent}
                    loading={isPending}
                  >
                    Aplicar a {items.length} contrato(s)
                  </Button>
                </>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default RenewModal;
